// ***************************************************************************************
// The API client.
//
// WHAT IS NOT HERE MATTERS AS MUCH AS WHAT IS. There is no call for selecting a tile, for
// checking a word, for an animation, for a move, or for an autosave. Play is entirely
// local: the board arrives once, the dictionary arrives once, and nothing else is fetched
// until the game ends or the player asks for the leaderboard.
// ***************************************************************************************

import type { PuzzleIdentity } from '../engine/game.ts';
import type { TileRef } from '../engine/modes.ts';

export interface LeaderboardEntry {
  rank: number;
  name: string;
  score: number;
  words: number;
  longestWordLength: number;
  cleared: boolean;
  tilesRemaining: number;
  verified: boolean;
  /** This is you. Marked by the server, which is the only side that knows. */
  you?: boolean;
}

export interface Leaderboard {
  /** Everybody who has finished this puzzle, not just those shown. */
  players: number;
  entries: LeaderboardEntry[];
  /** Your row, when you finished outside the listed top. */
  you: LeaderboardEntry | null;
}

export interface SubmitOutcome {
  accepted: boolean;
  verified: boolean;
  score: number;
  achievementsClaimed?: number;
  shufflesUsed?: number;
  personalBest: number;
  improved: boolean;
  rank: number;
  cleared: boolean;
  tilesRemaining: number;
  /** Everybody who has finished this puzzle, this player included. */
  players?: number;
  /** How many of them this score genuinely beat. Ties are not wins. */
  beaten?: number;
}

/**
 * "Beat 68% of players today", or null when that would be meaningless.
 *
 * A percentage of two other people is not a percentage, it is a rounding of a coin toss,
 * and "beat 100% of players" on a day with one other player reads as a boast the moment
 * somebody checks. Below the floor the finish screen says the plain rank instead.
 */
export const PERCENTILE_MIN_OTHERS = 5;

export function beatPercent(outcome: SubmitOutcome): number | null {
  const others = (outcome.players ?? 1) - 1;

  if (others < PERCENTILE_MIN_OTHERS) return null;

  return Math.round(((outcome.beaten ?? 0) / others) * 100);
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);

  if (!response.ok) {
    let detail: unknown = null;

    try {
      detail = await response.json();
    } catch {
      /* a non-JSON error body is not worth a second failure */
    }

    const message =
      (detail as { error?: string } | null)?.error ?? `request failed (${response.status})`;

    throw new ApiError(message, response.status, detail);
  }

  return (await response.json()) as T;
}

/**
 * Today's puzzle for THIS SITE.
 *
 * No mode is sent. Each deployment serves exactly one mode and decides which; asking for
 * a different one is how Ladder would leak onto the main site.
 */
export async function fetchTodaysPuzzle(): Promise<PuzzleIdentity> {
  return request<PuzzleIdentity>('/api/today');
}

export async function fetchPuzzle(puzzleId: string): Promise<PuzzleIdentity> {
  return request<PuzzleIdentity>(`/api/puzzle/${encodeURIComponent(puzzleId)}`);
}

/**
 * Today's board.
 *
 * The player id goes with the request so the server can mark which row is yours - it is
 * the only side that can, since the board carries no ids - and send your own row when you
 * placed below the listed top.
 */
export async function fetchLeaderboard(
  puzzleId: string,
  player?: string,
  /**
   * Skip the browser's cache.
   *
   * The board is served with a short cache lifetime, which is right for a player
   * refreshing it and wrong immediately after changing something: a re-fetch inside that
   * window is answered from cache with the OLD name, which looks precisely like the
   * rename having failed. Verified the hard way.
   */
  fresh = false,
): Promise<Leaderboard> {
  const query = player ? `?player=${encodeURIComponent(player)}` : '';

  const body = await request<{
    players?: number;
    entries: LeaderboardEntry[];
    you?: LeaderboardEntry;
  }>(
    `/api/leaderboard/${encodeURIComponent(puzzleId)}${query}`,
    fresh ? { cache: 'no-store' } : undefined,
  );

  return {
    players: body.players ?? body.entries.length,
    entries: body.entries,
    you: body.you ?? null,
  };
}

/**
 * Put a name on every score this player has posted, past ones included.
 *
 * Fire and forget: a name that fails to reach the server is a cosmetic loss, and the
 * next submitted result carries it anyway.
 */
export async function renamePlayer(playerId: string, name: string | null): Promise<void> {
  try {
    await request<{ ok: boolean }>('/api/name', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ playerId, name }),
    });
  } catch {
    /* see above */
  }
}

/**
 * Submit a finished game.
 *
 * The score is NOT sent - only the moves. Whatever comes back is the server's own
 * arithmetic, and the finish screen shows that rather than what the client computed.
 */
export async function submitResult(input: {
  puzzleId: string;
  playerId: string;
  name: string | null;
  /** The action log: a word is its tiles, a shuffle is `{ shuffle: true }`. */
  moves: readonly (readonly TileRef[] | { shuffle: true })[];
  rulesVersion: number;
  dictionaryVersion: string;
}): Promise<SubmitOutcome> {
  return request<SubmitOutcome>('/api/result', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      puzzleId: input.puzzleId,
      playerId: input.playerId,
      name: input.name,
      moves: input.moves,
      rulesVersion: input.rulesVersion,
      dictionaryVersion: input.dictionaryVersion,
    }),
  });
}
